import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Flex, Form, InputNumber, Select, Switch, Typography } from 'antd';
import { WorkflowEvent, WorkflowEventLabel } from '@leedpulse-monorepo/enums';
import { READ_ONLY_INPUT_STYLE } from '@leedpulse-monorepo/components';
import { AdminFieldInputProps } from '../../AdminFieldInput';
import getDomains from '../../../../../../domain/use_cases/domains/getDomains';
import getTags from '../../../../../../domain/use_cases/tags/getTags';
import Tag from '../../../../../../domain/models/Tag';

// Runtime config `workflows` (tab Workflows): settings of each contact workflow, common to every
// subscription. A subscription turns a workflow on for itself in `settings.workflows.<id>.enabled`.
interface WorkflowsSettingsProps extends AdminFieldInputProps {
  value: Record<string, unknown>;
}

const META_KEYS = new Set(['label', 'custom_field_component']);

// The Admins form only saves the keys the loaded doc holds (AdminFormFields pickBySchema): a field
// missing from the doc is not shown, it would look saved and be lost
const FIELDS = ['tag_ids', 'events', 'delay_minutes', 'disabled'];

// The workflows of the backend registry (helpers/workflows/workflows.registry.ts)
const WORKFLOW_LABELS: Record<string, string> = {
  'buyer-profile-crm': 'Fiche acquéreur dans le CRM (achat)',
  'renter-profile-crm': 'Fiche locataire dans le CRM (location)',
};

const EVENT_OPTIONS = Object.values(WorkflowEvent).map((event) => ({ label: WorkflowEventLabel[event], value: event }));

// Enough for every domain: there are a handful
const DOMAINS_LIMIT = 100;

const WorkflowsSettings: React.FC<WorkflowsSettingsProps> = ({ namePath, label, value, isReadOnly }) => {
  const [tagGroups, setTagGroups] = useState<{ label: string; options: { label: string; value: string }[] }[]>([]);
  const [loading, setLoading] = useState(true);

  // Global tags and the tags of each domain: the settings apply to every subscription, whatever its
  // domain. Tags of a single subscription are left out
  useEffect(() => {
    let bCancelled = false;
    const toOptions = (tags: Tag[]) => tags.filter((t) => !t.idSubscription).map((t) => ({ label: t.name, value: t.id }));
    (async () => {
      try {
        const { domains } = await getDomains(DOMAINS_LIMIT);
        const [globals, ...perDomain] = await Promise.all([getTags(null), ...domains.map((d) => getTags(d.id))]);
        const groups = [
          { label: 'Tags globaux', options: toOptions(globals.filter((t) => !t.idDomain)) },
          ...domains.map((d, i) => ({ label: d.name, options: toOptions(perDomain[i].filter((t) => t.idDomain === d.id)) })),
        ].filter((g) => g.options.length);
        if (!bCancelled) setTagGroups(groups);
      } catch (e) {
        console.log('error: ', e);
      } finally {
        if (!bCancelled) setLoading(false);
      }
    })();
    return () => {
      bCancelled = true;
    };
  }, []);

  const workflowIds = useMemo(() => Object.keys(value || {}).filter((k) => !META_KEYS.has(k)).sort(), [value]);
  const hasField = (sId: string, sField: string) => Object.prototype.hasOwnProperty.call(value?.[sId] || {}, sField);
  const missingFields = (sId: string) => FIELDS.filter((sField) => !hasField(sId, sField));

  return (
    <Flex vertical gap={16} style={{ gridColumn: '1/4', border: '1px solid #e0e0e0', padding: 16, borderRadius: 8 }}>
      <Typography.Title level={5} style={{ margin: 0 }}>{label}</Typography.Title>
      <Typography.Text type="secondary">
        Réglages communs à tous les abonnements. Un workflow ne tourne que pour les abonnements qui l&apos;ont activé
        (settings.workflows.&lt;id&gt;.enabled), et seulement sur les contacts qui portent l&apos;un des tags choisis.
      </Typography.Text>
      {workflowIds.map((sId) => (
        <Flex key={sId} vertical gap={8} style={{ border: '1px solid #f0f0f0', padding: 12, borderRadius: 8 }}>
          <Typography.Text strong>
            {WORKFLOW_LABELS[sId] || sId} <Typography.Text type="secondary" code>{sId}</Typography.Text>
          </Typography.Text>
          {missingFields(sId).length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={`Absent du document Admins, ne peut pas être enregistré ici : ${missingFields(sId).join(', ')}. L'ajouter en mode JSON.`}
            />
          )}
          {hasField(sId, 'tag_ids') && (
            <Form.Item label="Tags concernés" name={[...namePath, sId, 'tag_ids']} extra="Sans tag, le workflow ne tourne sur aucun contact.">
              <Select
                mode="multiple"
                options={tagGroups}
                loading={loading}
                disabled={isReadOnly}
                optionFilterProp="label"
                placeholder="Choisir les tags"
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              />
            </Form.Item>
          )}
          {hasField(sId, 'events') && (
            <Form.Item label="Déclencheurs" name={[...namePath, sId, 'events']} extra="Chaque déclencheur peut lancer une analyse IA.">
              <Select
                mode="multiple"
                options={EVENT_OPTIONS}
                disabled={isReadOnly}
                optionFilterProp="label"
                placeholder="Choisir les événements"
                style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
              />
            </Form.Item>
          )}
          <Flex gap={24} wrap="wrap">
            {hasField(sId, 'delay_minutes') && (
              <Form.Item label="Délai (minutes)" name={[...namePath, sId, 'delay_minutes']} extra="Les événements de cette fenêtre partent en une seule exécution.">
                <InputNumber min={1} disabled={isReadOnly} style={isReadOnly ? READ_ONLY_INPUT_STYLE : {}} />
              </Form.Item>
            )}
            {hasField(sId, 'disabled') && (
              <Form.Item label="Coupé partout" name={[...namePath, sId, 'disabled']} valuePropName="checked" extra="Interrupteur d'urgence : n'active jamais rien.">
                <Switch disabled={isReadOnly} />
              </Form.Item>
            )}
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
};

export default WorkflowsSettings;
